import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Mic, Square, Play, Download, Loader2, Sparkles, Volume2, Upload, User, Baby, Wand2, Coins } from "lucide-react";

const VOICE_TTS_CREDITS_COST = 1;

const CINEMA_PRESETS = [
  { key: "cinematic", label: "Cinéma", stability: 0.35, similarity: 0.85, style: 0.7, cinema: true },
  { key: "documentary", label: "Documentaire", stability: 0.65, similarity: 0.75, style: 0.3, cinema: false },
  { key: "energetic", label: "Énergique", stability: 0.3, similarity: 0.8, style: 0.85, cinema: true },
  { key: "neutral", label: "Neutre", stability: 0.5, similarity: 0.75, style: 0.3, cinema: false },
];

const CATEGORY_LABEL = {
  homme: { icon: User, label: "Hommes" },
  femme: { icon: User, label: "Femmes" },
  enfant: { icon: Baby, label: "Enfants" },
  custom: { icon: Sparkles, label: "Mes voix clonées" },
};

export default function VoiceStudio() {
  const { user, checkAuth } = useAuth();
  const [tab, setTab] = useState("generate"); // generate | record | clone
  const [voices, setVoices] = useState({ curated: [], custom: [] });
  const [voiceId, setVoiceId] = useState("EXAVITQu4vr4xnSDxMaL");
  const [category, setCategory] = useState("femme");
  const [text, setText] = useState("");
  const [preset, setPreset] = useState(CINEMA_PRESETS[0]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const credits = user?.credits ?? 0;
  const canAfford = credits >= VOICE_TTS_CREDITS_COST;

  // Recording
  const [recording, setRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [transcript, setTranscript] = useState("");
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const [scrollSpeed, setScrollSpeed] = useState(40); // px/s
  const teleprompterRef = useRef(null);

  // Clone
  const [cloneFile, setCloneFile] = useState(null);
  const [cloneName, setCloneName] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/voice/voices");
        setVoices(data);
      } catch (err) {
        console.warn("voices load failed:", err?.message);
      }
    })();
  }, []);

  // teleprompter auto-scroll while recording
  useEffect(() => {
    if (!recording || !teleprompterRef.current) return;
    const el = teleprompterRef.current;
    let raf;
    let last = performance.now();
    const tick = (t) => {
      const dt = (t - last) / 1000; last = t;
      el.scrollTop += scrollSpeed * dt;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [recording, scrollSpeed]);

  const filteredCurated = voices.curated.filter(v => v.category === category);
  const list = category === "custom" ? voices.custom : filteredCurated;

  const generate = async () => {
    if (!text.trim()) { toast.error("Saisissez le texte de la voix off"); return; }
    if (!canAfford) {
      toast.error(`Crédits insuffisants (${credits}/${VOICE_TTS_CREDITS_COST} requis)`);
      return;
    }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/voice/tts", {
        text, voice_id: voiceId,
        stability: preset.stability, similarity_boost: preset.similarity, style: preset.style,
        cinema_effect: preset.cinema,
      });
      setResult(data);
      const cost = data.credits_charged || 0;
      toast.success(cost > 0 ? `Voix off prête ! ${cost} crédit débité` : "Voix off prête !");
      checkAuth(); // refresh credits
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        toast.error(e.response.data?.detail || "Crédits insuffisants");
      } else {
        toast.error(e.response?.data?.detail || "Erreur TTS");
      }
    } finally { setLoading(false); }
  };

  const startRecording = async () => {
    if (!text.trim()) { toast.error("Écrivez d'abord le texte à lire"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setRecordedBlob(blob);
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRecRef.current = rec;
      rec.start();
      setRecording(true);
      if (teleprompterRef.current) teleprompterRef.current.scrollTop = 0;
    } catch (e) {
      toast.error("Accès au micro refusé");
    }
  };

  const stopRecording = () => {
    mediaRecRef.current?.stop();
    setRecording(false);
  };

  const transcribe = async () => {
    if (!recordedBlob) return;
    const fd = new FormData();
    fd.append("audio", recordedBlob, "rec.webm");
    setLoading(true);
    try {
      const { data } = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTranscript(data.text);
      toast.success("Transcription terminée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur transcription");
    } finally { setLoading(false); }
  };

  const cloneVoice = async () => {
    if (!cloneFile || !cloneName.trim()) { toast.error("Nom + fichier audio requis"); return; }
    const fd = new FormData();
    fd.append("audio", cloneFile);
    fd.append("voice_name", cloneName);
    setLoading(true);
    try {
      const { data } = await api.post("/voice/clone", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Voix "${data.name}" clonée !`);
      // refresh voices
      const v = await api.get("/voice/voices");
      setVoices(v.data);
      setCategory("custom");
      setVoiceId(data.voice_id);
      setCloneFile(null); setCloneName("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur clonage");
    } finally { setLoading(false); }
  };

  const download = () => {
    if (!result?.data_url) return;
    const a = document.createElement("a");
    a.href = result.data_url; a.download = `xookpub-voiceover-${Date.now()}.mp3`; a.click();
  };

  const downloadRecording = () => {
    if (!recordedBlob) return;
    const url = URL.createObjectURL(recordedBlob);
    const a = document.createElement("a"); a.href = url; a.download = `enregistrement-${Date.now()}.webm`; a.click();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="label-mono">Studio · Voix Off</span>
          <h1 className="mt-2 text-4xl font-heading tracking-tighter">Donnez une <span className="gold-text">voix</span> à votre marque</h1>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <div className="flex items-center gap-2 px-3 py-2 rounded-full border border-white/10">
            <Coins className="w-4 h-4 text-gold" />
            <span data-testid="voice-user-credits" className={canAfford ? "text-gold font-semibold" : "text-red-400 font-semibold"}>
              {credits} crédits
            </span>
            <span className="text-white/40">·</span>
            <span className="text-white/60">{VOICE_TTS_CREDITS_COST} / voix off</span>
          </div>
          {!canAfford && (
            <Link to="/pricing" data-testid="voice-buy-credits" className="ghost-btn !py-2 !px-3 text-xs">Recharger</Link>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-white/10">
        {[
          { key: "generate", label: "Générer voix IA", icon: Wand2 },
          { key: "record", label: "Enregistrer en live", icon: Mic },
          { key: "clone", label: "Cloner ma voix", icon: Sparkles },
        ].map(t => (
          <button key={t.key} data-testid={`voice-tab-${t.key}`} onClick={()=>setTab(t.key)}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition flex items-center gap-2 ${tab===t.key ? "border-gold text-gold" : "border-transparent text-white/60 hover:text-white"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "generate" && (
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 space-y-5">
            <div className="studio-card p-5">
              <label className="label-mono">Texte à dire</label>
              <textarea data-testid="voice-text" value={text} onChange={e=>setText(e.target.value)} maxLength={4000}
                placeholder="Écrivez le script de votre voix off ici..."
                className="mt-2 w-full h-44 bg-obsidian-light border border-white/10 rounded-lg p-3 outline-none focus:border-gold/50 resize-none" />
              <div className="mt-1 text-xs text-white/40 text-right">{text.length} / 4000</div>
            </div>

            <div className="studio-card p-5">
              <label className="label-mono">Catégorie de voix</label>
              <div className="mt-3 grid grid-cols-4 gap-2">
                {Object.entries(CATEGORY_LABEL).map(([key, info]) => (
                  <button key={key} data-testid={`category-${key}`} onClick={()=>setCategory(key)}
                    className={`p-3 rounded-lg border ${category===key ? "border-gold bg-gold/10 text-gold" : "border-white/10 hover:border-white/30"}`}>
                    <info.icon className="w-4 h-4 mx-auto mb-1" />
                    <div className="text-xs">{info.label}</div>
                  </button>
                ))}
              </div>

              <div className="mt-4 space-y-2 max-h-72 overflow-y-auto scroll-thin pr-1">
                {list.length === 0 && <div className="text-white/40 text-sm py-4 text-center">Aucune voix dans cette catégorie</div>}
                {list.map(v => (
                  <button key={v.voice_id} data-testid={`voice-${v.voice_id}`} onClick={()=>setVoiceId(v.voice_id)}
                    className={`w-full text-left p-3 rounded-lg border transition ${voiceId===v.voice_id ? "border-gold bg-gold/10" : "border-white/10 hover:border-white/30"}`}>
                    <div className="font-heading">{v.name}</div>
                    <div className="text-xs text-white/50 mt-0.5">{v.description}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="studio-card p-5">
              <label className="label-mono">Style / Effet</label>
              <div className="mt-3 grid grid-cols-4 gap-2">
                {CINEMA_PRESETS.map(p => (
                  <button key={p.key} data-testid={`preset-${p.key}`} onClick={()=>setPreset(p)}
                    className={`p-3 rounded-lg border text-sm ${preset.key===p.key ? "border-gold bg-gold/10 text-gold" : "border-white/10 hover:border-white/30"}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <button data-testid="voice-generate-btn" onClick={generate} disabled={loading || !canAfford} className="gold-btn w-full !py-4 text-base disabled:opacity-50">
              {loading
                ? <><Loader2 className="w-5 h-5 animate-spin" /> ElevenLabs synthétise...</>
                : <><Volume2 className="w-5 h-5" /> Générer la voix off · {VOICE_TTS_CREDITS_COST} crédit</>}
            </button>
          </div>

          <div className="lg:col-span-5">
            <div className="studio-card p-5 sticky top-24">
              <div className="flex items-center justify-between mb-4">
                <span className="label-mono">Lecteur</span>
                {result?.data_url && <button data-testid="voice-download" onClick={download} className="ghost-btn !py-2 !px-4 text-sm"><Download className="w-4 h-4" /> MP3</button>}
              </div>
              <div className="h-44 bg-obsidian rounded-xl border border-white/5 flex items-center justify-center">
                {loading ? (
                  <div className="text-center"><div className="w-10 h-10 rounded-full border-2 border-gold border-t-transparent animate-spin mx-auto" /><div className="mt-3 label-mono">Synthèse...</div></div>
                ) : result?.data_url ? (
                  <div className="w-full px-6"><audio src={result.data_url} controls className="w-full" data-testid="voice-result-audio" /></div>
                ) : (
                  <div className="text-center text-white/30"><Volume2 className="w-10 h-10 mx-auto" /><div className="mt-2 text-sm">Votre voix off apparaîtra ici</div></div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "record" && (
        <div className="space-y-5">
          <div className="studio-card p-5">
            <label className="label-mono">Texte à lire (téléprompter)</label>
            <textarea data-testid="record-text" value={text} onChange={e=>setText(e.target.value)} placeholder="Collez ici le script que vous souhaitez lire..." className="mt-2 w-full h-28 bg-obsidian-light border border-white/10 rounded-lg p-3 outline-none focus:border-gold/50 resize-none" />
          </div>

          <div className="studio-card p-0 overflow-hidden">
            <div className="bg-black h-80 relative">
              <div ref={teleprompterRef} className="absolute inset-0 overflow-hidden p-12 scroll-thin">
                <div className="teleprompter text-center" data-testid="teleprompter-text">
                  <div style={{ height: "40%" }}></div>
                  {text || "Saisissez votre texte au-dessus pour qu&apos;il apparaisse ici..."}
                  <div style={{ height: "60%" }}></div>
                </div>
              </div>
              {/* gold focus line */}
              <div className="pointer-events-none absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-gold/40" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black via-transparent to-black opacity-50" />

              {recording && (
                <div className="absolute top-4 left-4 flex items-center gap-3 bg-black/60 backdrop-blur px-3 py-2 rounded-full">
                  <div className="recording-pulse"></div>
                  <span className="text-xs uppercase tracking-widest text-red-400">REC</span>
                </div>
              )}
            </div>

            <div className="p-4 flex flex-wrap items-center gap-3 border-t border-white/5">
              {!recording ? (
                <button data-testid="start-recording" onClick={startRecording} className="gold-btn"><Mic className="w-4 h-4" /> Démarrer</button>
              ) : (
                <button data-testid="stop-recording" onClick={stopRecording} className="ghost-btn !border-red-400 !text-red-400"><Square className="w-4 h-4" /> Arrêter</button>
              )}
              <div className="flex items-center gap-2 ml-2">
                <span className="label-mono">Vitesse</span>
                <input data-testid="scroll-speed" type="range" min="15" max="120" value={scrollSpeed} onChange={(e)=>setScrollSpeed(+e.target.value)} className="accent-[#D4AF37]" />
                <span className="text-xs text-white/50 w-10">{scrollSpeed}px/s</span>
              </div>

              {recordedBlob && (
                <>
                  <audio src={URL.createObjectURL(recordedBlob)} controls className="ml-auto max-w-xs" data-testid="recorded-audio" />
                  <button data-testid="download-recording" onClick={downloadRecording} className="ghost-btn !py-2 !px-3 text-sm"><Download className="w-4 h-4" /></button>
                  <button data-testid="transcribe-btn" onClick={transcribe} disabled={loading} className="ghost-btn !py-2 !px-3 text-sm">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Transcrire"}
                  </button>
                </>
              )}
            </div>
          </div>

          {transcript && (
            <div className="studio-card p-5">
              <span className="label-mono">Transcription Whisper</span>
              <p className="mt-2 text-white/90" data-testid="transcript-result">{transcript}</p>
            </div>
          )}
        </div>
      )}

      {tab === "clone" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="studio-card p-6">
            <h3 className="font-heading text-xl">Cloner votre voix</h3>
            <p className="text-white/55 text-sm mt-2">Uploadez un échantillon audio clair (30 secondes à 3 minutes recommandé). ElevenLabs créera une voix synthétique reproduisant votre timbre.</p>

            <div className="mt-5 space-y-3">
              <input data-testid="clone-name" value={cloneName} onChange={e=>setCloneName(e.target.value)} placeholder="Nom de la voix (ex: Ma voix pro)" className="w-full bg-obsidian-light border border-white/10 rounded-lg px-3 py-3 outline-none focus:border-gold/50 text-sm" />

              <label className={`block border-2 border-dashed rounded-lg p-6 cursor-pointer transition ${cloneFile ? "border-gold/40 bg-gold/5" : "border-white/10 hover:border-gold/40"}`}>
                <input data-testid="clone-file" type="file" accept="audio/*" onChange={e=>setCloneFile(e.target.files?.[0] || null)} className="hidden" />
                <Upload className="w-6 h-6 mx-auto text-gold" />
                <div className="mt-2 text-center text-sm text-white/70">
                  {cloneFile ? cloneFile.name : "Glissez un fichier MP3/WAV/M4A"}
                </div>
              </label>

              <button data-testid="clone-submit" onClick={cloneVoice} disabled={loading} className="gold-btn w-full !py-3">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Clonage...</> : "Cloner la voix"}
              </button>
            </div>
          </div>

          <div className="studio-card p-6">
            <h3 className="font-heading text-xl">Mes voix clonées</h3>
            <div className="mt-4 space-y-2 max-h-96 overflow-y-auto scroll-thin">
              {(voices.custom || []).length === 0 && <div className="text-white/40 text-sm">Aucune voix clonée pour l'instant.</div>}
              {(voices.custom || []).map(v => (
                <div key={v.voice_id} className="p-3 rounded-lg border border-white/10 flex items-center justify-between" data-testid={`custom-voice-${v.voice_id}`}>
                  <div>
                    <div className="font-heading">{v.name}</div>
                    <div className="text-xs text-white/50">Cloné le {new Date(v.created_at).toLocaleDateString("fr-FR")}</div>
                  </div>
                  <button onClick={()=>{ setCategory("custom"); setVoiceId(v.voice_id); setTab("generate"); toast.success("Voix sélectionnée"); }} className="ghost-btn !py-1.5 !px-3 text-xs">
                    <Play className="w-3.5 h-3.5" /> Utiliser
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
